import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { suscripcionClinicaService } from '../services/suscripcionClinicaService'
import { clinicaService } from '../services/clinicaService'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../components/ui/Modal'
import Badge from '../components/ui/Badge'
import LoadingState from '../components/ui/LoadingState'
import EmptyState from '../components/ui/EmptyState'
import Input from '../components/ui/Input'
import Textarea from '../components/ui/Textarea'
import Listbox from '../components/ui/Listbox'
import { Building2, Plus, RefreshCw, AlertCircle, X, Check, Calendar, MapPin, Phone, Mail, Globe, History } from 'lucide-react'

export default function SuscripcionesClinicas() {
  const { user } = useAuth()
  const { isDueno } = usePermissions()

  const [suscripciones, setSuscripciones] = useState([])
  const [clinicasDisponibles, setClinicasDisponibles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showSubscribeModal, setShowSubscribeModal] = useState(false)
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false)
  const [selectedSuscripcion, setSelectedSuscripcion] = useState(null)
  const [selectedClinicaId, setSelectedClinicaId] = useState('')
  const [motivoBaja, setMotivoBaja] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showHistorial, setShowHistorial] = useState(false)

  useEffect(() => {
    if (isDueno && user) {
      fetchSuscripciones()
      fetchClinicasDisponibles()
    }
  }, [isDueno, user])

  const fetchSuscripciones = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await suscripcionClinicaService.getAll()
      
      if (response && response.success) {
        const data = response.data || []
        setSuscripciones(data)
      } else {
        const errorMsg = response?.error || 'Error al cargar suscripciones'
        setError(errorMsg)
        setSuscripciones([])
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Error de conexión'
      setError(errorMsg)
      setSuscripciones([])
    } finally {
      setLoading(false)
    }
  }

  const fetchClinicasDisponibles = async () => {
    try {
      const response = await suscripcionClinicaService.getClinicasDisponibles()
      
      if (response && response.success) {
        const data = response.data || []
        setClinicasDisponibles(data)
      } else {
        setClinicasDisponibles([])
      }
    } catch (err) {
      setClinicasDisponibles([])
    }
  }

  const handleSubscribe = async (e) => {
    e.preventDefault()
    if (!selectedClinicaId) {
      setError('Por favor selecciona una clínica')
      return
    }

    setSubmitting(true)
    try {
      const response = await suscripcionClinicaService.subscribe(selectedClinicaId)
      
      if (response.success) {
        setShowSubscribeModal(false)
        setSelectedClinicaId('')
        fetchSuscripciones()
        fetchClinicasDisponibles()
        setError(null)
      } else {
        setError(response.error || 'Error al suscribirse a la clínica')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al suscribirse')
    } finally {
      setSubmitting(false)
    }
  }

  const handleUnsubscribe = async (e) => {
    e.preventDefault()
    if (!selectedSuscripcion) return

    setSubmitting(true)
    try {
      const response = await suscripcionClinicaService.unsubscribe(
        selectedSuscripcion.id_suscripcion,
        motivoBaja || null
      )
      
      if (response.success) {
        setShowUnsubscribeModal(false)
        setSelectedSuscripcion(null)
        setMotivoBaja('')
        fetchSuscripciones()
        fetchClinicasDisponibles()
        setError(null)
      } else {
        setError(response.error || 'Error al darse de baja')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al darse de baja')
    } finally {
      setSubmitting(false)
    }
  }

  const openUnsubscribeModal = (suscripcion) => {
    setSelectedSuscripcion(suscripcion)
    setMotivoBaja('')
    setShowUnsubscribeModal(true)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (!isDueno) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-10 w-10 mx-auto text-red-600 dark:text-red-400 mb-4" />
          <p className="text-red-600 dark:text-red-400">Solo usuarios DUEÑO pueden acceder a esta página</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <LoadingState message="Cargando suscripciones..." />
  }

  const suscripcionesActivas = suscripciones.filter(s => s.estado === 'ACTIVA')
  const suscripcionesPendientes = suscripciones.filter(s => s.estado === 'PENDIENTE_APROBACION')
  const suscripcionesInactivas = suscripciones.filter(s => !['ACTIVA', 'PENDIENTE_APROBACION'].includes(s.estado))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Mis Suscripciones a Clínicas
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Gestiona tus suscripciones a clínicas veterinarias
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            variant="secondary"
            onClick={() => setShowHistorial(!showHistorial)}
            className="w-full sm:w-auto"
          >
            <History className="w-4 h-4 mr-2" />
            {showHistorial ? 'Ocultar' : 'Ver'} Historial
          </Button>
          <Button
            onClick={() => setShowSubscribeModal(true)}
            disabled={clinicasDisponibles.length === 0}
            className="w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Suscribirse a Clínica
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={() => {
                setError(null)
                fetchSuscripciones()
                fetchClinicasDisponibles()
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </div>
        </div>
      )}

      {/* Suscripciones Pendientes */}
      {suscripcionesPendientes.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Suscripciones Pendientes ({suscripcionesPendientes.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {suscripcionesPendientes.map((suscripcion) => (
              <Card key={suscripcion.id_suscripcion} className="card-hover border-yellow-300 dark:border-yellow-700">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {suscripcion.clinica?.nombre || 'Clínica'}
                      </h3>
                      <Badge variant="warning" className="mt-2">
                        Pendiente de Aprobación
                      </Badge>
                    </div>
                  </div>

                  {suscripcion.clinica && (
                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {suscripcion.clinica.ciudad && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          {suscripcion.clinica.ciudad}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      Solicitado: {formatDate(suscripcion.fecha_suscripcion)}
                    </div>
                  </div>

                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                    <p className="text-xs text-yellow-800 dark:text-yellow-200">
                      Esperando aprobación del veterinario admin de la clínica
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Suscripciones con suscripción vigente */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Suscripciones vigentes ({suscripcionesActivas.length})
        </h2>
        {suscripcionesActivas.length === 0 && !error ? (
          <EmptyState
            icon={Building2}
            title="No tienes suscripciones vigentes"
            description="Suscríbete a una clínica para comenzar a gestionar tus mascotas"
          />
        ) : null}
        
        {suscripcionesActivas.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {suscripcionesActivas.map((suscripcion) => (
              <Card key={suscripcion.id_suscripcion} className="card-hover">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {suscripcion.clinica?.nombre || 'Clínica'}
                      </h3>
                      <Badge variant="success" className="mt-2">
                        <Check className="w-3 h-3 mr-1" />
                        Vigente
                      </Badge>
                    </div>
                  </div>

                  {suscripcion.clinica && (
                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {suscripcion.clinica.ciudad && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          {suscripcion.clinica.ciudad}
                        </div>
                      )}
                      {suscripcion.clinica.telefono && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-2" />
                          {suscripcion.clinica.telefono}
                        </div>
                      )}
                      {suscripcion.clinica.email && (
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-2" />
                          {suscripcion.clinica.email}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      Suscrito desde: {formatDate(suscripcion.fecha_suscripcion)}
                    </div>
                  </div>

                  <Button
                    variant="danger"
                    size="sm"
                    className="w-full"
                    onClick={() => openUnsubscribeModal(suscripcion)}
                  >
                    Darse de Baja
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Historial de Suscripciones */}
      {showHistorial && suscripcionesInactivas.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Historial ({suscripcionesInactivas.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {suscripcionesInactivas.map((suscripcion) => (
              <Card key={suscripcion.id_suscripcion} className="opacity-75">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {suscripcion.clinica?.nombre || 'Clínica'}
                      </h3>
                      <Badge variant="secondary" className="mt-2">
                        {suscripcion.estado}
                      </Badge>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    <div>Suscrito: {formatDate(suscripcion.fecha_suscripcion)}</div>
                    {suscripcion.fecha_baja && (
                      <div>Dado de baja: {formatDate(suscripcion.fecha_baja)}</div>
                    )}
                    {suscripcion.motivo_baja && (
                      <div className="mt-2 text-xs italic">
                        Motivo: {suscripcion.motivo_baja}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Modal Suscribirse */}
      <Modal
        isOpen={showSubscribeModal}
        onClose={() => {
          setShowSubscribeModal(false)
          setSelectedClinicaId('')
          setError(null)
        }}
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Suscribirse a una Clínica
          </h2>
        </ModalHeader>
        <form onSubmit={handleSubscribe}>
          <ModalContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Seleccionar Clínica
                </label>
                <Listbox
                  value={selectedClinicaId}
                  onChange={setSelectedClinicaId}
                  options={clinicasDisponibles.map(clinica => ({
                    value: clinica.id_clinica,
                    label: `${clinica.nombre}${clinica.ciudad ? ` - ${clinica.ciudad}` : ''}`
                  }))}
                  placeholder="Selecciona una clínica"
                />
                {clinicasDisponibles.length === 0 && (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    No hay clínicas disponibles para suscribirse
                  </p>
                )}
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Nota:</strong> Al suscribirte, todas tus citas y fichas clínicas existentes 
                  se asociarán automáticamente con esta clínica.
                </p>
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowSubscribeModal(false)
                setSelectedClinicaId('')
                setError(null)
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!selectedClinicaId || submitting}
            >
              {submitting ? 'Suscribiendo...' : 'Suscribirse'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal Darse de Baja */}
      <Modal
        isOpen={showUnsubscribeModal}
        onClose={() => {
          setShowUnsubscribeModal(false)
          setSelectedSuscripcion(null)
          setMotivoBaja('')
          setError(null)
        }}
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Darse de Baja de la Clínica
          </h2>
        </ModalHeader>
        <form onSubmit={handleUnsubscribe}>
          <ModalContent>
            <div className="space-y-4">
              {selectedSuscripcion && (
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                    ¿Estás seguro de que deseas darte de baja de{' '}
                    <strong>{selectedSuscripcion.clinica?.nombre || 'esta clínica'}</strong>?
                  </p>
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800 dark:text-green-200">
                      <strong>Importante:</strong> Al darte de baja, conservarás acceso a toda tu 
                      información histórica (citas, fichas clínicas, etc.) generada por esta clínica.
                    </p>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Motivo de baja (opcional)
                </label>
                <Textarea
                  value={motivoBaja}
                  onChange={(e) => setMotivoBaja(e.target.value)}
                  placeholder="Indica el motivo de la baja (opcional)"
                  rows={3}
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowUnsubscribeModal(false)
                setSelectedSuscripcion(null)
                setMotivoBaja('')
                setError(null)
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={submitting}
            >
              {submitting ? 'Procesando...' : 'Confirmar Baja'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  )
}

